// Окно редактора формулы: визуальный ввод (MathLive) + палитры, код формулы в трёх представлениях (LaTeX,
// линейная запись Word, AsciiMath) с синхронизацией и распознаванием вставки любого формата, вид формулы
// (с номером / без номера - расчёт / в строке), знак после формулы, перенос по 4.6.2.5, экспликация «где»,
// проверка по СТО с автоисправлением, копирование для Word.
import { useEffect, useMemo, useRef, useState } from 'react';
import { convertLatexToAsciiMath, convertAsciiMathToLatex, type MathfieldElement } from 'mathlive';
import type { DialogProps } from '../../core/registry';
import type { WhereItem } from '../../core/schema/math';
import { Btn, Modal } from '../../ui/controls';
import { MathField } from './MathField';
import { WhereEditor } from './where';
import { PaletteBody, PAL_TABS, type PalTab } from './Palette';
import { canon, hints, isCalcLine, latexToLinear, breakPoints, breakAt, lineCount, removeBreaks, trailingPunct,
  setTrailingPunct, linearizeFractions, type MathHint } from './latex';
import { convertLinear } from './unicodemath';
import { anyToLatex, detectFormat, FORMAT_NAMES } from './detect';
import { insertIntoActive, knownSymbols } from './state';
import { renderTex, toMathML } from './ml';
import { readPref, writePref } from '../../core/store';

export type Mode = 'num' | 'calc' | 'inline';
export interface FormulaResult { latex: string; numbered: boolean; where: WhereItem[]; display: boolean }

type SrcTab = 'latex' | 'linear' | 'ascii';

export function FormulaDialog({ ctx, props, close }: DialogProps<FormulaResult>) {
  const [latex, setLatex] = useState<string>(props.latex || '');
  const [mode, setMode] = useState<Mode>(props.display === false ? 'inline' : props.numbered === false ? 'calc' : 'num');
  const [where, setWhere] = useState<WhereItem[]>(props.where || []);
  const [src, setSrc] = useState<SrcTab>('latex');
  const [pal, setPal] = useState<PalTab>(() => (props.pal as PalTab) || (readPref('math.pal', 'struct') as PalTab));
  const [note, setNoteRaw] = useState('');
  const noteTimer = useRef(0);
  const setNote = (t: string) => { setNoteRaw(t); window.clearTimeout(noteTimer.current); noteTimer.current = window.setTimeout(() => setNoteRaw(''), 5000); };
  const [brkOpen, setBrkOpen] = useState(false);
  const modeTouched = useRef(props.latex ? true : false);
  const mfRef = useRef<MathfieldElement | null>(null);
  const known = useMemo(() => knownSymbols(ctx.editor, props.pos ?? undefined), []);

  const c = useMemo(() => canon(latex), [latex]);
  const hs: MathHint[] = useMemo(() => hints(c.latex, { inline: mode === 'inline', numbered: mode === 'num' }), [c.latex, mode]);
  // строка расчёта (числа подставлены) - без номера (4.6.1.8), пока пользователь сам не выбрал вид
  useEffect(() => {
    if (!modeTouched.current && mode === 'num' && isCalcLine(c.latex)) setMode('calc');
  }, [c.latex]);
  const pickMode = (m: Mode) => { modeTouched.current = true; setMode(m); };
  const pick = (t: string) => { if (!insertIntoActive(t)) { mfRef.current?.focus(); insertIntoActive(t); } };
  const setPalTab = (t: PalTab) => { setPal(t); writePref('math.pal', t); };

  const ok = () => {
    if (!c.latex.trim()) { close(); return; }
    close({ latex: c.latex, numbered: mode === 'num', where: mode === 'inline' ? [] : where.filter((w) => w.sym.trim() || String(w.desc || '').trim()), display: mode !== 'inline' });
  };
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); ok(); } };
    document.addEventListener('keydown', key, true);
    return () => document.removeEventListener('keydown', key, true);
  });

  const pts = useMemo(() => (mode === 'inline' ? [] : breakPoints(c.latex).sort((a, b) => a.prio - b.prio || a.index - b.index)), [c.latex, mode]);
  const lines = lineCount(c.latex);
  const punct = trailingPunct(c.latex);
  const num = props.label || '(N)';

  return (
    <Modal title={props.title || (mode === 'inline' ? 'Формула в строке' : 'Формула')} onClose={() => close()} width={1080} testid="formula-dialog" footer={<>
      <CopyMenu latex={c.latex} display={mode !== 'inline'} />
      <span className="mx-grow" />
      <span className="muted mx-small">Enter или Ctrl+Enter - готово</span>
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="Готово" primary onClick={ok} testid="formula-ok" />
    </>}>
      <div className="mx-dlg">
        <aside className="mx-pal" onMouseDown={(e) => { if ((e.target as HTMLElement).tagName !== 'INPUT') e.preventDefault(); }}>
          <div className="mx-pal-tabs">
            {PAL_TABS.map(([id, t]) => (
              <button key={id} type="button" className={'mx-pal-tab' + (pal === id ? ' is-active' : '')} onClick={() => setPalTab(id)} data-testid={'pal-' + id}>{t}</button>
            ))}
          </div>
          <div className="mx-pal-body"><PaletteBody tab={pal} onPick={pick} /></div>
        </aside>
        <main className="mx-main">
          {props.image && (
            <div className="mx-ref">
              <div className="muted mx-small">Формула-картинка - наберите её, картинка будет заменена формулой:</div>
              <img src={props.image} alt="формула-картинка" />
            </div>
          )}
          <div className={'mx-field-row' + (mode === 'inline' ? ' is-inline' : '')}>
            <MathField value={latex} onChange={setLatex} onEnter={ok} kind="dialog" autoFocus selectPlaceholder testid="math-field"
              fieldRef={(m) => { mfRef.current = m; }} onPasteFormat={(f) => setNote('Распознано: ' + (FORMAT_NAMES as any)[f])} />
            {mode === 'num' && <span className="mx-num" title="Номер ставится автоматически">{num}</span>}
          </div>
          <SourceBox tab={src} setTab={setSrc} latex={latex} setLatex={setLatex} onNote={setNote} />
          {note && <div className="mx-note">{note}</div>}
          <Hints notes={c.notes} hints={hs} onFix={(code) => {
            if (code === 'inline-bulky-num') pickMode('num');
            if (code === 'inline-bulky-lin') setLatex(linearizeFractions(c.latex));
            if (code === 'calc-numbered') pickMode('calc');
          }} />
          <div className="mx-opts">
            <div className="mx-seg" role="radiogroup" aria-label="Вид формулы">
              <button type="button" className={mode === 'num' ? 'is-on' : ''} onClick={() => pickMode('num')} data-testid="mode-num"
                title="Отдельной строкой с номером у правого поля (4.6.1.2, 4.6.1.6)">С номером {num}</button>
              <button type="button" className={mode === 'calc' ? 'is-on' : ''} onClick={() => pickMode('calc')} data-testid="mode-calc"
                title="Отдельной строкой без номера: строка расчёта (4.6.1.8)">Без номера (расчёт)</button>
              <button type="button" className={mode === 'inline' ? 'is-on' : ''} onClick={() => pickMode('inline')} data-testid="mode-inline"
                title="Короткая формула внутри текста (4.6.1.1)">В строке</button>
            </div>
            {mode !== 'inline' && <>
              <div className="mx-seg mx-seg-sm" title="Знак препинания после формулы (4.6.1.3). Запятую перед «где» и между формулами подряд редактор ставит сам">
                <span className="mx-seg-l">Знак после:</span>
                {['', ',', '.', ';'].map((p) => (
                  <button key={p || 'none'} type="button" className={punct === p ? 'is-on' : ''} onClick={() => setLatex(setTrailingPunct(c.latex, p))}>{p || 'нет'}</button>
                ))}
              </div>
              <div className="mx-brk">
                <button type="button" className="btn" onClick={() => setBrkOpen(!brkOpen)} disabled={!pts.length && lines < 2} data-testid="break-menu"
                  title="Перенос длинной формулы (4.6.2.5): на знаке соотношения, затем на + и −, затем на ×; знак повторяется в начале строки">
                  Перенос{lines > 1 ? ` (${lines} стр.)` : ''} ▾</button>
                {brkOpen && (
                  <div className="mx-brk-menu" onMouseLeave={() => setBrkOpen(false)}>
                    {lines > 1 && <button type="button" onClick={() => { setLatex(removeBreaks(c.latex)); setBrkOpen(false); }}>Убрать переносы</button>}
                    {pts.map((p) => (
                      <button key={p.index} type="button" onClick={() => { setLatex(breakAt(c.latex, p.index)); setBrkOpen(false); }}>
                        <span className={'mx-prio mx-prio-' + p.prio}>{p.prio === 1 ? 'лучше' : p.prio === 2 ? 'можно' : 'в крайнем случае'}</span> после «{p.label}»
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>}
          </div>
          {mode !== 'inline' && <WhereEditor latex={c.latex} value={where} onChange={setWhere} known={known} />}
        </main>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ код формулы
function SourceBox({ tab, setTab, latex, setLatex, onNote }: {
  tab: SrcTab; setTab: (t: SrcTab) => void; latex: string; setLatex: (s: string) => void; onNote: (s: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (tab === 'latex' ? latex : tab === 'linear' ? latexToLinear(latex) : safeAscii(latex));
  const apply = (v: string) => {
    setDraft(v);
    if (tab === 'latex') setLatex(v);
    else if (tab === 'linear') setLatex(canon(convertLinear(v)).latex);
    else { try { setLatex(canon(convertAsciiMathToLatex(v)).latex); } catch { /* */ } }
  };
  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const s = /msEquation|<m:oMath|<math[\s>]/i.test(html) ? html : text;
    const f = detectFormat(s);
    const expected = tab === 'latex' ? 'latex' : tab === 'linear' ? 'linear' : 'asciimath';
    if (f === expected || f === 'text') return;
    e.preventDefault();
    const r = anyToLatex(s, f);
    setDraft(null);
    setLatex(r.latex);
    setTab('latex');
    onNote('Распознано: ' + FORMAT_NAMES[f] + ' → LaTeX');
  };
  return (
    <div className="mx-src">
      <div className="mx-src-tabs">
        {([['latex', 'LaTeX'], ['linear', 'Линейная запись Word'], ['ascii', 'AsciiMath']] as [SrcTab, string][]).map(([id, t]) => (
          <button key={id} type="button" className={tab === id ? 'is-active' : ''} onClick={() => { setDraft(null); setTab(id); }} data-testid={'src-' + id}>{t}</button>
        ))}
        <span className="mx-grow" />
        <span className="muted mx-small">вставьте формулу из Word, MathML, LaTeX или текстом - формат распознается сам</span>
      </div>
      <textarea className="mono" rows={2} value={shown} spellCheck={false} onChange={(e) => apply(e.target.value)}
        onBlur={() => setDraft(null)} onPaste={onPaste} data-testid={tab === 'latex' ? 'formula-latex' : 'formula-src-' + tab}
        placeholder={tab === 'latex' ? '\\frac{a}{b}' : tab === 'linear' ? 'a/b, x^2, √(x), ∑_(i=1)^n▒x_i' : 'a/b, sqrt(x), sum_(i=1)^n x_i'} />
    </div>
  );
}

function safeAscii(latex: string) { try { return convertLatexToAsciiMath(latex); } catch { return ''; } }

// ------------------------------------------------------------------------------------------------ подсказки СТО
function Hints({ notes, hints: hs, onFix }: { notes: { code: string; msg: string }[]; hints: MathHint[]; onFix: (code: string) => void }) {
  if (!notes.length && !hs.length) return <div className="mx-hints mx-ok" data-testid="math-hints">✓ Оформление по СТО: греческие буквы, функции и константы прямым шрифтом, минус «−», десятичная запятая - при экспорте</div>;
  return (
    <div className="mx-hints" data-testid="math-hints">
      {notes.map((n) => <div key={n.code} className="mx-hint mx-hint-fix">✓ Исправлено: {n.msg}</div>)}
      {hs.map((h) => (
        <div key={h.code} className={'mx-hint mx-hint-' + h.sev}>
          <span>{h.sev === 'E' ? '✖' : h.sev === 'W' ? '⚠' : 'ℹ'} {h.msg}{h.rule ? ` (${h.rule})` : ''}</span>
          {h.code === 'inline-bulky' && <>
            <button type="button" className="mx-link" onClick={() => onFix('inline-bulky-num')}>отдельной строкой</button>
            <button type="button" className="mx-link" onClick={() => onFix('inline-bulky-lin')}>дробь через «/»</button>
          </>}
          {h.code === 'calc-numbered' && <button type="button" className="mx-link" onClick={() => onFix('calc-numbered')}>без номера</button>}
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ копирование
export function CopyMenu({ latex, display }: { latex: string; display: boolean }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState('');
  const copy = async (what: string, text: string) => {
    try { await navigator.clipboard.writeText(text); setDone(what); setTimeout(() => setDone(''), 1500); } catch { setDone('не удалось'); }
    setOpen(false);
  };
  return (
    <div className="mx-copy">
      <button type="button" className="btn" onClick={() => setOpen(!open)} disabled={!latex} title="Скопировать формулу">{done ? '✓ ' + done : 'Копировать ▾'}</button>
      {open && (
        <div className="mx-brk-menu mx-up" onMouseLeave={() => setOpen(false)}>
          <button type="button" onClick={() => copy('для Word', toMathML(latex, display))} title="MathML: вставьте в Word (Ctrl+V) - получится формула Word">Для Word (MathML)</button>
          <button type="button" onClick={() => copy('LaTeX', latex)}>LaTeX</button>
          <button type="button" onClick={() => copy('линейная', latexToLinear(latex))} title="Линейная запись: вставьте в поле формулы Word и нажмите «Профессиональный»">Линейная запись Word</button>
        </div>
      )}
    </div>
  );
}

export function previewHtml(latex: string, display = true) { return renderTex(latex, display); }
